import { useCallback, useEffect, useState } from 'react'
import {
  Box, Button, Checkbox, HStack, NumberInput, Spinner, Stack, Text, VStack,
  type NumberInputValueChangeDetails,
} from '@chakra-ui/react'
import { useQueryClient } from '@tanstack/react-query'
import { toaster } from '@/components/ui/toaster'
import {
  useCampaignViewSettings, useChaseCallList, useChaseSettings, useChaseSummary, useCutChaseBatch,
  usePrepareChase,
} from '../../../../hooks/useFollowUp'
import { useJobPolling } from '../../../../hooks/useJobPolling'
import { useLimits } from '../../../../hooks/useLimits'
import { extractApiMessage } from '../../../../lib/apiError'
import { CallBatchesPanel } from '../calls/CallBatchesPanel'
import { FollowUpRouteActions } from '../routes/FollowUpRouteActions'
import { ChaseSettingsModal } from './ChaseSettingsModal'
import { propensityLabel } from './chaseLabels'
import {
  FOLLOW_UP_KIND_LABELS,
  type ChasePrepareResultDto, type ChaseSettingsDto, type FollowUpMode, type FollowUpRouteCutResultDto,
} from '../../../../types/followUp'

/** A real instant (not a date-only value) in the viewer's zone: "Oct 24, 2:00 PM". */
function formatInstant(value: string): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
  }).format(new Date(value))
}

/** One line summarizing the widening thresholds (the organizer's own numbers). */
function thresholdsSummary(s: ChaseSettingsDto | undefined): string {
  if (!s) return ''
  const parts: string[] = []
  if (s.ideologyMin !== null || s.ideologyMax !== null) {
    parts.push(`Ideology ${s.ideologyMin ?? 0}–${s.ideologyMax ?? 100}`)
  }
  if (s.propensityMin !== null) parts.push(`${propensityLabel(s.electionType)} ≥ ${s.propensityMin}`)
  if (s.activismMin !== null) parts.push(`Activism ≥ ${s.activismMin}`)
  return parts.length > 0 ? parts.join(' · ') : 'No thresholds set'
}

interface Prepared {
  mode: FollowUpMode
  result: ChasePrepareResultDto
}

export interface ChaseSectionProps {
  campaignId: string
}

/** CF-1 P5: the Follow-up tab's "Chase to vote" actions — live counts, the voted-data freshness
 *  line, the opt-in widening, and the prepare → confirm → hand-off flows for drive routes and call
 *  lists. Slotted beside P4's Chase script editor via mergeSectionSlots. */
export function ChaseSection({ campaignId }: ChaseSectionProps) {
  const queryClient = useQueryClient()
  // Local only, never saved: each visit starts with widening off.
  const [includeWidened, setIncludeWidened] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)

  const { data: settings } = useChaseSettings(campaignId)
  const { data: summary, isLoading, error } = useChaseSummary(campaignId, { includeWidened })
  const { data: viewSettings } = useCampaignViewSettings(campaignId)
  const finalPush = viewSettings?.effectiveMode === 'FinalPush'

  const { mutateAsync: prepare, isPending: isPreparePending } = usePrepareChase(campaignId)
  const { mutateAsync: cut, isPending: isCutPending } = useCutChaseBatch(campaignId)
  const { mutateAsync: makeCallList, isPending: isCallListPending } = useChaseCallList(campaignId)

  // Caps are pre-flight only: while limits load there is no clamp.
  const maxDrivers = useLimits().data?.inputCaps?.maxFollowUpCutDrivers
  const clampDrivers = useCallback((n: number) => {
    const v = Number.isFinite(n) ? Math.max(1, Math.round(n)) : 1
    return maxDrivers !== undefined ? Math.min(v, maxDrivers) : v
  }, [maxDrivers])
  const [driversText, setDriversText] = useState('1')
  const drivers = clampDrivers(Number(driversText))

  const [prepareJobId, setPrepareJobId] = useState<string | null>(null)
  const [pendingMode, setPendingMode] = useState<FollowUpMode | null>(null)
  const [prepared, setPrepared] = useState<Prepared | null>(null)
  const [cutJobId, setCutJobId] = useState<string | null>(null)
  const [serverError, setServerError] = useState<string | null>(null)

  const prepareJob = useJobPolling(prepareJobId)
  const cutJob = useJobPolling(cutJobId)
  const isPreparing = isPreparePending || (!!prepareJobId && !prepareJob.isTerminal)
  const isCutting = isCutPending || (!!cutJobId && !cutJob.isTerminal)
  const busy = isPreparing || isCutting || isCallListPending

  // Prepare job: terminal transition fires once, then the poll resets.
  useEffect(() => {
    if (!prepareJobId || !prepareJob.isTerminal) return
    const job = prepareJob.data
    if (job?.status === 'Succeeded' && pendingMode) {
      // Invalidate on the job's success, never at the 202 accept (job-polling rules).
      queryClient.invalidateQueries({ queryKey: ['followUp', campaignId, 'chase', 'summary'] })
      queryClient.invalidateQueries({ queryKey: ['followUp', campaignId, 'items'] })
      setPrepared({ mode: pendingMode, result: job.result as ChasePrepareResultDto })
    } else {
      setServerError(job?.errorMessage ?? 'Could not prepare the chase batch.')
    }
    setPrepareJobId(null)
    setPendingMode(null)
    // queryClient/campaignId are stable for the mount; re-running on them would re-fire.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prepareJob.isTerminal, prepareJob.data, prepareJobId])

  // Cut job: on success refresh P2's routes, the itinerary pool and the follow-up tab counts.
  useEffect(() => {
    if (!cutJobId || !cutJob.isTerminal) return
    const job = cutJob.data
    if (job?.status === 'Succeeded') {
      queryClient.invalidateQueries({ queryKey: ['followUp', campaignId, 'routes'] })
      queryClient.invalidateQueries({ queryKey: ['followUp', campaignId, 'summary'] })
      queryClient.invalidateQueries({ queryKey: ['campaigns', campaignId, 'itinerary'] })
      const result = job.result as FollowUpRouteCutResultDto | undefined
      const routeCount = result?.routeCount ?? 0
      const skipped = result?.skippedItemCount ?? 0
      if (routeCount === 0 && skipped > 0) {
        toaster.create({
          title: `None of these could be routed — ${skipped} have no mappable address or can't be contacted.`,
          type: 'info',
          duration: 7000,
        })
      } else if (routeCount === 0) {
        toaster.create({ title: 'Nothing left to cut — these were already routed.', type: 'info', duration: 5000 })
      } else {
        toaster.create({ title: routeCount === 1 ? '1 route cut' : `${routeCount} routes cut`, type: 'success', duration: 4000 })
      }
    } else {
      setServerError(job?.errorMessage ?? 'Route cut failed.')
    }
    setCutJobId(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cutJob.isTerminal, cutJob.data, cutJobId])

  const handlePrepare = async (mode: FollowUpMode) => {
    setServerError(null)
    setPrepared(null)
    try {
      const { jobId } = await prepare({ mode, includeWidened })
      setPendingMode(mode)
      setPrepareJobId(jobId)
    } catch (err) {
      setServerError(extractApiMessage(err, 'Could not prepare the chase batch.'))
    }
  }

  // A 409 means the batch is spent: drop it so the organizer prepares again. A 400 keeps it so the
  // same hand-off can be retried — that succeeds only when the backend released the batch (a
  // downstream route-cut / call-list refusal). The "everyone in this batch has voted or been
  // closed" 400 leaves it consumed, so a retry there gets the 409 and then asks for a new prepare.
  const handOffFailed = (err: unknown, fallback: string) => {
    setServerError(extractApiMessage(err, fallback))
    const status = (err as { response?: { status?: number } })?.response?.status
    if (status === 409) setPrepared(null)
  }

  const handleConfirm = async () => {
    if (!prepared) return
    setServerError(null)
    const batchId = prepared.result.batchId
    if (prepared.mode === 'Route') {
      try {
        const { jobId } = await cut({ batchId, driverCount: drivers })
        setPrepared(null)
        setCutJobId(jobId)
      } catch (err) {
        handOffFailed(err, 'Could not cut routes.')
      }
    } else {
      try {
        await makeCallList(batchId)
        setPrepared(null)
        toaster.create({ title: 'Call list ready', type: 'success', duration: 4000 })
      } catch (err) {
        handOffFailed(err, 'Could not make the call list.')
      }
    }
  }

  const freshness = () => {
    // Always visible: a neutral placeholder while the counts load or after they fail.
    if (!summary) {
      return <Text fontSize="sm" color="muted">{isLoading ? 'Checking voted data…' : 'Voted data status unavailable'}</Text>
    }
    if (!summary.electionSet) {
      return <Text fontSize="sm" color="muted">Set the target election to drop people who've voted</Text>
    }
    if (!summary.votedDataAsOf) {
      return <Text fontSize="sm" color="muted">No voted data loaded for this election yet</Text>
    }
    return <Text fontSize="sm" color="muted">Voted data as of {formatInstant(summary.votedDataAsOf)}</Text>
  }

  const count = (label: string, value: number | null) => (
    <Text fontSize="sm" color="muted">
      {label}{' '}
      <Text as="span" fontWeight="semibold" color="fg" css={{ fontVariantNumeric: 'tabular-nums' }}>
        {value === null ? '—' : value}
      </Text>
    </Text>
  )

  const widened = summary?.widenedCount
  const fullOnMobile = { base: 'full', md: 'auto' } as const

  return (
    <VStack align="stretch" gap={4} w="full" minW={0}>
      {/* The widening opt-in leads the section; under Final push it carries the accent. */}
      <Box
        data-testid="chase-widening"
        data-emphasis={finalPush ? 'true' : 'false'}
        borderWidth="1px"
        borderColor={finalPush ? 'accent' : 'border'}
        bg={finalPush ? 'accent.soft' : undefined}
        borderRadius="md"
        p={3}
        w="full"
      >
        <Checkbox.Root
          colorPalette="brand"
          minH="44px"
          checked={includeWidened}
          onCheckedChange={(d) => setIncludeWidened(d.checked === true)}
        >
          <Checkbox.HiddenInput />
          <Checkbox.Control><Checkbox.Indicator /></Checkbox.Control>
          <Checkbox.Label fontWeight={finalPush ? 'semibold' : undefined}>
            Also include likely supporters who haven't voted
          </Checkbox.Label>
        </Checkbox.Root>
        {includeWidened && (
          <Stack direction={{ base: 'column', md: 'row' }} gap={2} mt={2} align={{ base: 'stretch', md: 'center' }}>
            {widened !== null && widened !== undefined && (
              <Text fontSize="sm" fontWeight="semibold" css={{ fontVariantNumeric: 'tabular-nums' }}>
                {`+${widened}${summary?.widenedCapped ? '+' : ''}`}
              </Text>
            )}
            <Text fontSize="sm" color="muted" flex="1" minW={0}>{thresholdsSummary(settings)}</Text>
            <Button size="sm" variant="ghost" colorPalette="brand" minH="44px" onClick={() => setSettingsOpen(true)}>
              Edit
            </Button>
          </Stack>
        )}
      </Box>

      {isLoading ? (
        <VStack py={4}><Spinner color="accent" /></VStack>
      ) : !!error || !summary ? (
        <Text color="danger.fg" fontSize="sm">{extractApiMessage(error, 'Could not load chase counts.')}</Text>
      ) : (
        <VStack align="stretch" gap={1}>
          <Stack data-testid="chase-counts" direction={{ base: 'column', md: 'row' }} gap={{ base: 1, md: 4 }}>
            {count('Pledged or supporter', summary.pledgedOrSupporter)}
            {count('Already voted', summary.alreadyVoted)}
            {count('Left to chase', summary.leftToChase)}
          </Stack>
          <Text fontSize="xs" color="muted">Updated {formatInstant(summary.computedAt)}</Text>
        </VStack>
      )}

      <Stack direction={{ base: 'column', md: 'row' }} gap={2} align={{ base: 'stretch', md: 'center' }}>
        {freshness()}
        <Button size="sm" variant="outline" minH="44px" w={fullOnMobile} onClick={() => setSettingsOpen(true)}>
          Settings
        </Button>
      </Stack>

      <Stack direction={{ base: 'column', md: 'row' }} gap={2} align={{ base: 'stretch', md: 'center' }}>
        <HStack gap={2}>
          <NumberInput.Root
            size="sm"
            w="88px"
            min={1}
            max={maxDrivers}
            value={driversText}
            onValueChange={(d: NumberInputValueChangeDetails) => setDriversText(d.value)}
          >
            <NumberInput.Input aria-label="Drivers" minH="44px" />
            <NumberInput.Control>
              <NumberInput.IncrementTrigger />
              <NumberInput.DecrementTrigger />
            </NumberInput.Control>
          </NumberInput.Root>
          <Text fontSize="sm">drivers</Text>
        </HStack>
        <Button colorPalette="brand" size="sm" minH="44px" w={fullOnMobile} disabled={busy}
          onClick={() => handlePrepare('Route')}>
          Cut drive routes
        </Button>
        <Button variant="outline" size="sm" minH="44px" w={fullOnMobile} disabled={busy}
          onClick={() => handlePrepare('Call')}>
          Make call list
        </Button>
      </Stack>

      {isPreparing && (
        <HStack gap={2} role="status"><Spinner size="sm" color="accent" /><Text fontSize="sm">Preparing…</Text></HStack>
      )}
      {isCutting && (
        <HStack gap={2} role="status"><Spinner size="sm" color="accent" /><Text fontSize="sm">Cutting routes…</Text></HStack>
      )}

      {!!prepared && (
        <Box borderWidth="1px" borderColor="border" borderRadius="md" p={3} w="full" minW={0}>
          <Text fontSize="sm" mb={3}>
            {`${prepared.result.itemCount} people ready · ${prepared.result.excludedVoted} already voted · `
              + `${prepared.result.excludedRecentlyContacted} contacted in the last ${settings?.recontactDays ?? 3} days. `
              + (prepared.mode === 'Route' ? `Cut into ${drivers} routes?` : 'Make a call list?')}
          </Text>
          <Stack direction={{ base: 'column', md: 'row' }} gap={2}>
            <Button colorPalette="brand" size="sm" minH="44px" w={fullOnMobile} disabled={busy} onClick={handleConfirm}>
              {prepared.mode === 'Route' ? `Cut into ${drivers} routes` : 'Make the call list'}
            </Button>
            <Button variant="ghost" size="sm" minH="44px" w={fullOnMobile} disabled={busy}
              onClick={() => { setPrepared(null); setServerError(null) }}>
              Cancel
            </Button>
          </Stack>
        </Box>
      )}

      {!!serverError && <Text fontSize="sm" color="danger.fg" role="alert">{serverError}</Text>}

      {/* Ruling R18: cut chase routes are listed, previewed, assigned and re-cut with P2's own route
          UI (query key ['followUp', id, 'routes', 'Chase', null] — the cut success above invalidates
          it). Its cut row is hidden: this section's prepare → confirm → cut is the only chase cut. */}
      <FollowUpRouteActions campaignId={campaignId} kind="Chase" hideCut />

      {/* Ruling R24: chase call lists are listed, assigned, viewed and deleted with P4's own panel (query
          key ['followUp', id, 'callBatches', { kind: 'Chase' }] — the call-list hand-off above invalidates
          the ['followUp', id, 'callBatches'] prefix). Creation is hidden: "Make call list" is the only path. */}
      <CallBatchesPanel campaignId={campaignId} kind="Chase" kindLabel={FOLLOW_UP_KIND_LABELS.Chase} hideCreate />

      <ChaseSettingsModal
        isOpen={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        campaignId={campaignId}
        settings={settings}
      />
    </VStack>
  )
}
